import './style.scss';
import { useAuth, useBasic } from 'contexts';
import { useCallback, useEffect, useState, useRef } from 'react';
import {
  getDailyAccessData,
  getHourlyAccessData,
  getChallengesData,
  getTotalCompletedCourses,
  getTotalGames,
  getMonthlyActiveUsers,
  getMonthlyActiveUsersUniques,
  getMonthlyChallenges,
  getTotalPlayers,
} from 'services';
import Chart from 'chart.js/auto';

interface Period {
  title: string;
  start?: string;
  end?: string;
  custom?: boolean;
}

export default function AccessReportsPage() {
  const { appearance } = useBasic();
  const { profileUser } = useAuth();
  const chartRef = useRef<HTMLCanvasElement>(null);
  const [chart, setChart] = useState<Chart | null>(null);

  const [periods] = useState<Period[]>([
    { title: 'Mês Atual', start: '-0M-', end: '-0M+' },
    { title: 'Mês Passado', start: '-1M-', end: '-1M+' },
    { title: 'Mês Retrasado', start: '-2M-', end: '-2M+' },
    { title: 'Data personalizada', custom: true },
  ]);

  const [choice, setChoice] = useState<{ period: Period }>({ period: periods[0] });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [all, setAll] = useState<any[]>([]);
  const [challenges, setChallenges] = useState<any[]>([]);
  const [totalConluiramJornadas, setTotalConluiramJornadas] = useState<any[]>([]);
  const [totalGames, setTotalGames] = useState<any[]>([]);
  const [monthlyActiveUsers, setMonthlyActiveUsers] = useState(0);
  const [monthlyActiveUsersUniques, setMonthlyActiveUsersUniques] = useState(0);
  const [monthlyChallenges, setMonthlyChallenges] = useState(0);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [dailyAverage, setDailyAverage] = useState(0);

  const isMenuActive = useCallback(
    (pathname: string) => {
      if (appearance?.menu && Object.keys(appearance?.menu as any).includes(pathname)) {
        return (appearance?.menu as any)[pathname];
      }
      return true;
    },
    [appearance],
  );

  useEffect(() => {
    if (profileUser?.roles?.includes('admin') === false) {
      window.location.href = '/';
    } else if (!isMenuActive('reports')) {
      window.location.href = '/';
    }
  }, [isMenuActive, profileUser]);

  const onInit = useCallback(
    async (custom = false) => {
      if (!choice.period.custom) {
        setStartDate('');
        setEndDate('');
      }
      await Promise.all([
        loadAllActive(),
        countMonthlyChallenges(),
        countMonthlyActiveUsers(),
        countMonthlyActiveUsersUniques(),
        countAllPlayers(),
        loadTotalConcluiramJornadas(),
        loadTotalGames(),
      ]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [choice.period.custom, startDate, endDate],
  );

  const loadAllActive = async () => {
    try {
      const data = await getDailyAccessData(startDate, endDate, choice.period);
      setAll(data);
      const totalDays = data.length;
      const totalUsers = data.reduce((a: number, b: any) => a + b.total, 0);
      setDailyAverage(totalUsers / totalDays);
      await loadChallenges();
    } catch (error) {
      console.log(error);
    }
  };

  const loadHourlyActive = async () => {
    try {
      const data = await getHourlyAccessData(startDate, endDate, choice.period);
      setAll(data);
      await loadHourlyChallenges();
    } catch (error) {
      console.log(error);
    }
  };

  const loadChallenges = async () => {
    try {
      const data = await getChallengesData(startDate, endDate, choice.period);
      setChallenges(data);
      generateChart();
    } catch (error) {
      console.log(error);
    }
  };

  const loadHourlyChallenges = async () => {
    try {
      const data = await getChallengesData(startDate, endDate, choice.period, true);
      setChallenges(data);
      generateChart();
    } catch (error) {
      console.log(error);
    }
  };

  const loadTotalConcluiramJornadas = async () => {
    try {
      const data = await getTotalCompletedCourses(startDate, endDate, choice.period);
      setTotalConluiramJornadas(data);
    } catch (error) {
      console.log(error);
    }
  };

  const loadTotalGames = async () => {
    try {
      const data = await getTotalGames(startDate, endDate, choice.period);
      setTotalGames(data);
    } catch (error) {
      console.log(error);
    }
  };

  const countMonthlyActiveUsers = async () => {
    try {
      const total = await getMonthlyActiveUsers(startDate, endDate, choice.period);
      setMonthlyActiveUsers(total);
    } catch (error) {
      console.log(error);
    }
  };

  const countMonthlyActiveUsersUniques = async () => {
    try {
      const total = await getMonthlyActiveUsersUniques(startDate, endDate, choice.period);
      setMonthlyActiveUsersUniques(total);
    } catch (error) {
      console.log(error);
    }
  };

  const countMonthlyChallenges = async () => {
    try {
      const total = await getMonthlyChallenges(startDate, endDate, choice.period);
      setMonthlyChallenges(total);
    } catch (error) {
      console.log(error);
    }
  };

  const countAllPlayers = async () => {
    try {
      const total = await getTotalPlayers();
      setTotalPlayers(total);
    } catch (error) {
      console.log(error);
    }
  };

  const generateChart = useCallback(() => {
    if (!chartRef.current) return;

    const dateSet = new Set(all.map(item => item.date));
    challenges.forEach(item => dateSet.add(item._id.time));
    const labels = Array.from(dateSet).sort();

    const valuesLogin = labels.map(label => {
      const value = all.find(item => item.date === label);
      return value ? value.total : 0;
    });

    const valuesChallenges = labels.map(label => {
      const value = challenges.find(item => item._id.time === label);
      return value ? value.total : 0;
    });

    if (chart) {
      chart.destroy();
    }

    const newChart = new Chart(chartRef.current, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Acessos',
            data: valuesLogin,
            borderColor: '#4bac04',
            backgroundColor: 'rgba(75, 192, 192, 0.2)',
          },
          {
            label: 'Desafios',
            data: valuesChallenges,
            borderColor: '#ff6384',
            backgroundColor: 'rgba(255, 99, 132, 0.2)',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
      },
    });

    setChart(newChart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, challenges]);

  const returnGameName = (gameCode: string) => {
    switch (gameCode) {
      case 'game_crossword':
        return 'Palavras Cruzadas';
      case 'game_wordsearch':
        return 'Caça-Palavras';
      case 'game_codex':
        return 'Codex';
      case 'game_enigma':
        return 'Enigma';
      case 'game_runner':
        return 'Runner';
      case 'game_detective':
        return 'Detetive';
      case 'game_story':
        return 'História';
      case 'game_puzzle':
        return 'Quebra-Cabeça';
      case 'game_memory':
        return 'Memória';
      case 'game':
        return 'Jogo';
      default:
        return 'Jogo não encontrado';
    }
  };

  useEffect(() => {
    onInit();
  }, [onInit]);

  useEffect(() => {
    if (all.length || challenges.length) {
      generateChart();
    }
  }, [all, challenges, generateChart]);

  const handleExportPDF = useCallback(() => {
    const graficoElement = document.getElementById('grafico');
    const infosElement = document.getElementById('infos');

    if (!graficoElement || !infosElement) return;

    // Capturar o canvas e converter para imagem
    const canvas = graficoElement.querySelector('canvas');
    const graficoHTML = canvas
      ? `<div id="grafico" style="margin-bottom: 30px; page-break-after: always;">
           <img src="${canvas.toDataURL('image/png')}" style="max-width: 100%; height: auto;" />
         </div>`
      : graficoElement.outerHTML;

    // Criar uma nova janela para impressão
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Relatório de Acessos</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            #infos {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 20px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
            }
            td, th { 
              padding: 8px; 
              border: 1px solid #ddd; 
            }
            h4 { 
              margin-top: 0; 
            }
            .bg-white {
              background-color: white;
              padding: 16px;
              border-radius: 8px;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            }
            .space-y-4 > * + * {
              margin-top: 16px;
            }
            @media print {
              body { margin: 0; }
              #grafico img { 
                max-width: 100%; 
                height: auto; 
              }
            }
          </style>
        </head>
        <body>
          ${graficoHTML}
          ${infosElement.outerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();

    // Aguardar o carregamento e imprimir
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  }, []);

  return (
    <div className="p-4">
      <div className="mb-4">
        <h4 className="text-lg font-bold">Acessos</h4>
        <p className="text-gray-600">As informações mostradas inicialmente são referentes ao mês atual</p>
      </div>

      <div className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <select
              value={periods.findIndex(p => p.title === choice.period.title)}
              onChange={e => {
                const period = periods[Number(e.target.value)];
                setChoice({ period });
                if (!period.custom) {
                  onInit();
                }
              }}
              className="w-full p-2 border rounded"
            >
              {periods.map((period, index) => (
                <option key={index} value={index}>
                  {period.title}
                </option>
              ))}
            </select>
          </div>
          {choice.period.custom && (
            <>
              <div>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="Data Inicio"
                />
              </div>
              <div>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="Data Fim"
                />
              </div>
              <div>
                <button onClick={() => onInit(true)} className="bg-blue-500 text-white px-4 py-2 rounded">
                  Filtrar
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="mb-4">
        <h3>
          Acessos: <span className="text-warning">{monthlyActiveUsers}</span>
        </h3>
      </div>

      <div className="mb-4">
        <button onClick={loadAllActive} className="bg-sky-600 text-white px-4 py-2 rounded mr-2">
          Por dia
        </button>
        <button onClick={loadHourlyActive} className="bg-sky-600 text-white px-4 py-2 rounded mr-2">
          Por hora
        </button>
        <button onClick={handleExportPDF} className="bg-green-500 text-white px-4 py-2 rounded float-right">
          Exportar PDF
        </button>
      </div>

      <div id="grafico" className="mb-8 bg-gray-100 p-6 rounded" style={{ height: '450px' }}>
        <canvas ref={chartRef}></canvas>
      </div>

      <div id="infos" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div>
          <h4 className="text-lg font-bold mb-4">Taxa de utilização da plataforma</h4>
          <div className="bg-white p-4 rounded shadow">
            <table className="w-full table-auto">
              <tbody>
                <tr>
                  <td>Público-Alvo:</td>
                  <td>{totalPlayers}</td>
                </tr>
                <tr>
                  <td>Acessaram o Curso:</td>
                  <td>{monthlyActiveUsersUniques}</td>
                </tr>
                <tr>
                  <td>Engajamento Padrão:</td>
                  <td>{((monthlyActiveUsersUniques / totalPlayers) * 100).toFixed(2)}%</td>
                </tr>
                <tr>
                  <td>Média Diária de Acessos:</td>
                  <td>
                    {dailyAverage.toFixed(0)} ({((dailyAverage / totalPlayers) * 100).toFixed(2)}%)
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h4 className="text-lg font-bold mb-4">Total de cursos concluídos</h4>
          <div className="space-y-4">
            {totalConluiramJornadas.map((obj, index) => (
              <div key={index} className="bg-white p-4 rounded shadow">
                <p className="font-bold">{obj.curso}</p>
                <table className="w-full table-auto">
                  <tbody>
                    <tr>
                      <td>Total</td>
                      <td>{obj.total}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-lg font-bold mb-4">Total de games jogados</h4>
          <div className="bg-white p-4 rounded shadow">
            <table className="w-full table-auto">
              <thead>
                <tr>
                  <th className="p-2 text-left">Game</th>
                  <th className="p-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {totalGames.map((obj, index) => (
                  <tr key={index}>
                    <td className="p-2 text-left">{returnGameName(obj._id)}</td>
                    <td className="p-2 text-center">{obj.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h4 className="text-lg font-bold mb-4 mt-6">Desafios conquistados</h4>
          <div className="bg-white p-4 rounded shadow">
            <table className="w-full table-auto">
              <tbody>
                <tr>
                  <td>Total</td>
                  <td>{monthlyChallenges}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
